import { ComponentProps } from 'react'

import { ModalPortal } from '@cash-web/shared-feature-modal'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { BlockerLoading } from './BlockerLoading/BlockerLoading'
import { FlowRenderer } from './FlowRenderer'

/**
 * Renders a modal that is used when working with a flow, typically a blocker flow.
 *
 * The `FlowModal` component utilizes the `useFlowContext` hook to manage flow states and actions.
 * It conditionally renders based on the flow's progress, loading state, or presence of children components.
 * If no flow is in progress, no loading state is present, and no children are provided, it renders nothing.
 *
 * @param {ReactNode} children - The content to be rendered inside the modal. If not provided, `FlowRenderer` is used.
 * @param {boolean} [loading=false] - Indicates if the modal should display a loading state. Defaults to `false`.
 * @param {Function} onClose - Callback function to be called when the modal is requested to be closed.
 * @returns {ReactNode|null} The `ModalPortal` component with either a loading indicator, custom children, or the `FlowRenderer` based on the provided props and flow state. Returns `null` if no content should be rendered.
 */
export const FlowModal = ({
  children,
  loading = false,
  onClose,
  ...props
}: ComponentProps<typeof ModalPortal> & { loading?: boolean }) => {
  const flow = useFlowContext()
  if (!flow.isInProgress && !loading && !children) {
    return null
  }
  return (
    <ModalPortal
      {...props}
      onClose={() => {
        flow.cancel()
        onClose?.()
      }}
    >
      {loading ? <BlockerLoading /> : children ? children : <FlowRenderer />}
    </ModalPortal>
  )
}
